import { FC } from "react";
import Link from "next/link";
import { AsyncReturnType } from "type-fest";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { Area } from "@prisma/client";

import SubCategories from "./sub-categories";

import {
  CategoryWithProductCount,
  CategoryWithSuppliers,
  Props as FilterProps,
} from "@interfaces/categories";
import BreadCrumbs from "@components/common/breadcrumbs";
import Layout from "@components/common/layout";
import ResponsiveSlider from "@components/common/responsive-slider";
import TitleDescriptionHeader from "@components/common/title-description-header";
import FiltersComponent from "@components/products/filters-component";
import ProductsGrid from "@components/products/products-grid";
import { Filters, Measures } from "@interfaces/filters";
import { Facets } from "@interfaces/utility";
import { ReviewWithProduct } from "@interfaces/review";
import ProductReviewCard from "@components/common/product-review-card";
import { getParentCategories } from "@lib/category";
import { usePage } from "@hooks/use-page";
import SupplierCard from "@components/common/supplier-card";

interface Props
  extends Omit<
    FilterProps &
      Pick<
        CategoryWithSuppliers,
        | "slug"
        | "description"
        | "descriptionEn"
        | "onlinePurchase"
        | "supplierToCategory"
        | "name"
        | "nameEn"
      >,
    "filters" | "facets"
  > {
  title: string;
  filters?: Filters;
  ranges?: Measures;
  facets?: Facets;
  parentCategories: AsyncReturnType<typeof getParentCategories>;
  categories?: CategoryWithProductCount[];
  reviews?: ReviewWithProduct[];
  areaData: Area[];
}

const CategoryFilterPage: FC<Props> = ({
  filters,
  parentCategories,
  products,
  slug,
  description: descriptionMe,
  facets,
  totalNumberOfProducts,
  ranges,
  categories,
  reviews,
  onlinePurchase,
  supplierToCategory,
  name,
  nameEn,
  descriptionEn,
  areaData,
}) => {
  const pageNumber = usePage();
  const { locale, pathname } = useRouter();
  const { t } = useTranslation("category");

  const title = locale === "en" ? nameEn : name;
  const description = locale === "en" ? descriptionEn : descriptionMe;

  const body = onlinePurchase ? (
    <>
      <ProductsGrid {...{ products, pageNumber, totalNumberOfProducts }} />
      <div className="py-32">
        <div className="mx-auto max-w-[90vw]">
          <ResponsiveSlider
            {...{
              breakpoints: { xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, base: 1 },
            }}
          >
            {reviews &&
              reviews.length &&
              reviews.map((review) => (
                <div key={review.id} className="w-full">
                  <ProductReviewCard {...review} />
                </div>
              ))}
          </ResponsiveSlider>
        </div>
      </div>
    </>
  ) : (
    <div className="flex flex-wrap max-w-screen-xl mx-auto px-10 justify-center items-center">
      {supplierToCategory.map(({ featuredPhoto, supplier: { name, url } }) => (
        <div key={name} className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4">
          <SupplierCard {...{ featuredPhoto, name, url, showName: true }} />
        </div>
      ))}
    </div>
  );

  return (
    <Layout {...{ title, description }}>
      <div className="pt-2">
        <BreadCrumbs
          breadcrumbs={[
            ...parentCategories
              .map((c, i) => ({
                name: c.name,
                path: `${i === 0 ? "categories/" : ""}${c.slug}`,
              }))
              .reverse(),
            title,
          ]}
        />
        <TitleDescriptionHeader {...{ title, description }} />
        {onlinePurchase ? (
          <>
            {!pathname.includes("search") ? (
              <SubCategories {...{ categories }} />
            ) : null}
            <FiltersComponent
              {...{ filters, facets, totalNumberOfProducts, ranges, areaData }}
            />
          </>
        ) : (
          <div className="text-center">
            <Link
              href={{
                pathname: "/inquiry/[category]",
                query: { category: slug },
              }}
            >
              <a className="font-semibold hover:underline flex items-center justify-center flex-col">
                <span>{t("inquiry.inquiry")}</span>
                <span className="uppercase underline">{t("inquiry.here")}</span>
              </a>
            </Link>
          </div>
        )}
      </div>
      {body}
    </Layout>
  );
};

export default CategoryFilterPage;
